import { useStore } from '@tanstack/react-store'
import { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { resetPaymentsList, paymentsListStore } from 'shared/store/paymentsList'
import { getAllFilterParams } from 'shared/utils/getFilterParams'
import {
  getURLFromFilterParams,
  isSameParams,
} from 'shared/utils/getURLFromFilterParams'
import { getDateTimeToSubmit } from 'shared/utils/dateFormatting'
import { paramsToTableStateAdapter, tableStateToParamsAdapter } from 'shared/utils/sortAdapter'
import { ReactTablePaged } from 'uikit/ReactTable'
import {
  Container,
  SnippetsContainer,
  SnippetsContainerMain,
  SnippetsContainerRightAside,
} from 'uikit/layout'
import { useFetchStats } from './hooks/useFetchStats'
import { useFetchPaymentsList } from './hooks/useFetchPaymentsList'
import { PaymentsFilter } from './PaymentsFilter'
import { columns } from './PaymentsList.config'

export const PaymentsList = () => {
  const { params } = useParams()
  const history = useHistory()
  const urlParams = getAllFilterParams(params)

  useEffect(() => {
    paymentsListStore.setState((state) => {
      if (isSameParams(params, state.filter)) {
        return state
      }
      return {
        ...state,
        filter: getAllFilterParams(params),
        page: 0,
      }
    })
  }, [params])

  const { page, totalPages, sort, totalElements, filter } = useStore(paymentsListStore)
  const { mutate: fetchStats, data: stats, isPending: statsIsLoading } = useFetchStats()
  const { data, isFetching: isLoading } = useFetchPaymentsList({ urlParams })

  useEffect(() => {
    return () => resetPaymentsList()
  }, [])

  const onPageChange = (page) => {
    paymentsListStore.setState((state) => ({
      ...state,
      page: page - 1,
    }))
  }

  const onSortChange = (state) => {
    const newSort = tableStateToParamsAdapter(state)

    paymentsListStore.setState((state) => ({
      ...state,
      sort: newSort,
    }))
  }

  const onFilterChange = (nextFilter) => {
    paymentsListStore.setState((state) => ({
      ...state,
      filter: nextFilter,
    }))

    const urlFilterParams = getURLFromFilterParams(nextFilter)
    const newPath = `/payments${urlFilterParams}`

    if (history.location.pathname !== newPath) {
      history.push(newPath)
    }
  }

  const onUpdateStats = () => {
    const { paymentDateFrom, paymentDateTo, ...rest } = filter

    fetchStats({
      ...rest,
      paymentDateFrom: paymentDateFrom ? getDateTimeToSubmit(paymentDateFrom) : undefined,
      paymentDateTo: paymentDateTo ? getDateTimeToSubmit(paymentDateTo) : undefined,
    })
  }

  return (
    <Container>
      <SnippetsContainer indentTop={70}>
        <SnippetsContainerMain>
          <ReactTablePaged
            storageId="payments-list"
            onRowClick={(row) => history.push(`/payments/show/0/${row.id}`)}
            currentPage={page + 1}
            totalPages={totalPages}
            isLoading={isLoading}
            columns={columns}
            data={data?.payments || []}
            initialState={{
              sorting: paramsToTableStateAdapter(sort),
            }}
            onPageChange={onPageChange}
            onSortingChange={onSortChange}
          />
        </SnippetsContainerMain>
        <SnippetsContainerRightAside>
          <PaymentsFilter
            totalItems={totalElements}
            value={filter}
            onChange={onFilterChange}
            stats={stats}
            statsIsLoading={statsIsLoading}
            onUpdateStats={onUpdateStats}
          />
        </SnippetsContainerRightAside>
      </SnippetsContainer>
    </Container>
  )
}
